import React, { useEffect, useState } from 'react';
import { CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import EditRoleAssignmentForm from './forms/EditRoleAssignmentForm';
import { useFetchRoles } from '../hooks/useFetchRoles';
import { useParams } from 'react-router-dom';
import { API } from '@/utils/API';

interface RoleAssignmentData {
  id: string;
  user_id: string;
  role_key: string;
  resource_type?: string;
  resource_id?: string;
  user: {
    id: string;
    email_address: string;
  };
  resource?: {
    id: string;
    name: string;
  } | null;
}

const EditRoleAssignmentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const {
    roles,
    isLoading: isLoadingRoles,
    error: rolesError,
    fetchRoles,
    clearError,
  } = useFetchRoles();
  const [roleAssignment, setRoleAssignment] =
    useState<RoleAssignmentData | null>(null);
  const [isLoadingAssignment, setIsLoadingAssignment] = useState(true);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    const fetchRoleAssignment = async () => {
      if (!id) {
        setAssignmentError('Role assignment ID is required');
        setIsLoadingAssignment(false);
        return;
      }

      setIsLoadingAssignment(true);
      setAssignmentError(null);

      try {
        const response = await API.authenticated.admin.fetch(
          `/role_assignments/${id}`
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
              `Failed to load role assignment: ${response.status}`
          );
        }

        const data = await response.json();
        setRoleAssignment(data.role_assignment);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to load role assignment';
        setAssignmentError(errorMessage);
        console.error('Error fetching role assignment:', error);
      } finally {
        setIsLoadingAssignment(false);
      }
    };

    fetchRoleAssignment();
  }, [id]);

  const handleRetry = () => {
    clearError();
    setAssignmentError(null);
    fetchRoles();
    if (id) {
      setIsLoadingAssignment(true);
      // Re-fetch role assignment
      const fetchRoleAssignment = async () => {
        try {
          const response = await API.authenticated.admin.fetch(
            `/role_assignments/${id}`
          );
          if (response.ok) {
            const data = await response.json();
            setRoleAssignment(data.role_assignment);
          }
        } catch (error) {
          console.error('Error fetching role assignment:', error);
        } finally {
          setIsLoadingAssignment(false);
        }
      };
      fetchRoleAssignment();
    }
  };

  // Sanitize error message for better user experience
  const getErrorMessage = (error: string | null): string => {
    if (!error) return '';

    // Common error messages that should be user-friendly
    if (error.includes('Network Error') || error.includes('fetch')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    if (error.includes('401') || error.includes('Unauthorized')) {
      return 'You are not authorized to perform this action. Please refresh the page and try again.';
    }

    if (error.includes('404') || error.includes('not found')) {
      return 'Role assignment not found. It may have been deleted.';
    }

    if (error.includes('500') || error.includes('Internal Server Error')) {
      return 'Something went wrong on our end. Please try again in a few moments.';
    }

    // For other errors, show a generic message to avoid exposing technical details
    return 'An unexpected error occurred. Please try again.';
  };

  const isLoading = isLoadingRoles || isLoadingAssignment;
  const error = rolesError || assignmentError;

  return (
    <div className="min-h-screen flex justify-center p-4">
      <div className="w-full max-w-4xl">
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">
                  {isLoadingRoles
                    ? 'Loading roles...'
                    : 'Loading role assignment...'}
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
                <div className="text-destructive mb-4">
                  <svg
                    className="mx-auto h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-destructive mb-2">
                  {rolesError
                    ? 'Failed to load roles'
                    : 'Failed to load role assignment'}
                </h3>
                <p className="text-destructive/80 mb-4">
                  {getErrorMessage(error)}
                </p>
                <Button
                  onClick={handleRetry}
                  variant="outline"
                  className="border-destructive/30 text-destructive hover:bg-destructive/10"
                >
                  Try Again
                </Button>
              </div>
            </div>
          ) : roleAssignment ? (
            <EditRoleAssignmentForm
              roles={roles}
              roleAssignment={roleAssignment}
            />
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No role assignment data available
              </p>
            </div>
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default EditRoleAssignmentPage;
